import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosOverarchingService } from '@/services/RorosService';
import { rorosKeys } from './keys';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Company Profile ───────────────────────────────────────────────────────

export function useRorosCompanyProfile() {
  return useQuery({
    queryKey: rorosKeys.overarching.companyProfile,
    queryFn: () => RorosOverarchingService.getCompanyProfile(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosCompanyProfileUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosOverarchingService.updateCompanyProfile(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.companyProfile });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

// ─── Overarching Aggregates ────────────────────────────────────────────────

export function useRorosOverarchingAll() {
  return useQuery({
    queryKey: rorosKeys.overarching.all,
    queryFn: () => RorosOverarchingService.getAll(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosOverarchingCompletion() {
  return useQuery({
    queryKey: rorosKeys.overarching.completion,
    queryFn: () => RorosOverarchingService.getCompletion(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Core Values ───────────────────────────────────────────────────────────

export function useRorosCoreValues(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.overarching.coreValues, params] as const,
    queryFn: () => RorosOverarchingService.coreValues.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosCoreValue(id: string) {
  return useQuery({
    queryKey: rorosKeys.overarching.coreValue(id),
    queryFn: () => RorosOverarchingService.coreValues.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosCoreValueCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosOverarchingService.coreValues.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.coreValues });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

export function useRorosCoreValueUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosOverarchingService.coreValues.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.coreValues });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.coreValue(id) });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
    },
  });
}

export function useRorosCoreValueRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosOverarchingService.coreValues.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.coreValues });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

export function useRorosCoreValueReorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id: string; sortOrder: number }[]) => RorosOverarchingService.coreValues.reorder(items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.coreValues });
    },
  });
}

// ─── Company Principles ────────────────────────────────────────────────────

export function useRorosCompanyPrinciples(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.overarching.companyPrinciples, params] as const,
    queryFn: () => RorosOverarchingService.companyPrinciples.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosCompanyPrinciple(id: string) {
  return useQuery({
    queryKey: rorosKeys.overarching.companyPrinciple(id),
    queryFn: () => RorosOverarchingService.companyPrinciples.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosCompanyPrincipleCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosOverarchingService.companyPrinciples.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.companyPrinciples });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

export function useRorosCompanyPrincipleUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosOverarchingService.companyPrinciples.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.companyPrinciples });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.companyPrinciple(id) });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
    },
  });
}

export function useRorosCompanyPrincipleRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosOverarchingService.companyPrinciples.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.companyPrinciples });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

export function useRorosCompanyPrincipleReorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id: string; sortOrder: number }[]) => RorosOverarchingService.companyPrinciples.reorder(items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.companyPrinciples });
    },
  });
}

// ─── USPs ──────────────────────────────────────────────────────────────────

export function useRorosUsps(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.overarching.usps, params] as const,
    queryFn: () => RorosOverarchingService.usps.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosUsp(id: string) {
  return useQuery({
    queryKey: rorosKeys.overarching.usp(id),
    queryFn: () => RorosOverarchingService.usps.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosUspCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosOverarchingService.usps.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.usps });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

export function useRorosUspUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosOverarchingService.usps.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.usps });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.usp(id) });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
    },
  });
}

export function useRorosUspRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosOverarchingService.usps.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.usps });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

export function useRorosUspReorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id: string; sortOrder: number }[]) => RorosOverarchingService.usps.reorder(items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.usps });
    },
  });
}

// ─── Target Markets ────────────────────────────────────────────────────────

export function useRorosTargetMarkets(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.overarching.targetMarkets, params] as const,
    queryFn: () => RorosOverarchingService.targetMarkets.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosTargetMarket(id: string) {
  return useQuery({
    queryKey: rorosKeys.overarching.targetMarket(id),
    queryFn: () => RorosOverarchingService.targetMarkets.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosTargetMarketCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosOverarchingService.targetMarkets.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.targetMarkets });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

export function useRorosTargetMarketUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosOverarchingService.targetMarkets.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.targetMarkets });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.targetMarket(id) });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
    },
  });
}

export function useRorosTargetMarketRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosOverarchingService.targetMarkets.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.targetMarkets });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

export function useRorosTargetMarketReorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id: string; sortOrder: number }[]) => RorosOverarchingService.targetMarkets.reorder(items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.targetMarkets });
    },
  });
}

// ─── Personas ──────────────────────────────────────────────────────────────

export function useRorosPersonas(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.overarching.personas, params] as const,
    queryFn: () => RorosOverarchingService.personas.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosPersona(id: string) {
  return useQuery({
    queryKey: rorosKeys.overarching.persona(id),
    queryFn: () => RorosOverarchingService.personas.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosPersonaCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosOverarchingService.personas.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.personas });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

export function useRorosPersonaUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosOverarchingService.personas.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.personas });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.persona(id) });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
    },
  });
}

export function useRorosPersonaRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosOverarchingService.personas.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.personas });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

export function useRorosPersonaReorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id: string; sortOrder: number }[]) => RorosOverarchingService.personas.reorder(items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.personas });
    },
  });
}

// ─── ICPs ──────────────────────────────────────────────────────────────────

export function useRorosIcps(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.overarching.icps, params] as const,
    queryFn: () => RorosOverarchingService.icps.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosIcp(id: string) {
  return useQuery({
    queryKey: rorosKeys.overarching.icp(id),
    queryFn: () => RorosOverarchingService.icps.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosIcpCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosOverarchingService.icps.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.icps });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

export function useRorosIcpUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosOverarchingService.icps.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.icps });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.icp(id) });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
    },
  });
}

export function useRorosIcpRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosOverarchingService.icps.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.icps });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

export function useRorosIcpReorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id: string; sortOrder: number }[]) => RorosOverarchingService.icps.reorder(items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.icps });
    },
  });
}

// ─── ICP-Persona Links ─────────────────────────────────────────────────────

export function useRorosIcpPersonaLinks(params?: { icpId?: string; personaId?: string }) {
  const queryParams: Record<string, string> = {};
  if (params?.icpId) queryParams.icpId = params.icpId;
  if (params?.personaId) queryParams.personaId = params.personaId;

  return useQuery({
    queryKey: [...rorosKeys.overarching.icpPersonaLinks, params] as const,
    queryFn: () => RorosOverarchingService.icpPersonaLinks.list(queryParams),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosIcpPersonaLinkCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosOverarchingService.icpPersonaLinks.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.icpPersonaLinks });
    },
  });
}

export function useRorosIcpPersonaLinkRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosOverarchingService.icpPersonaLinks.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.icpPersonaLinks });
    },
  });
}

export function useRorosIcpPersonaLinkReorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id: string; sortOrder: number }[]) =>
      RorosOverarchingService.icpPersonaLinks.reorder(items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.icpPersonaLinks });
    },
  });
}

// Vision Goals hooks removed: roros_vision_goals table dropped in migration 381 (Wave A).
// Use useRorosOutcomes with { parent_type: 'vision' } to display Vision-anchored outcomes.

// ─── SWT Items ─────────────────────────────────────────────────────────────

export function useRorosSwtItems(params?: Record<string, string>) {
  // SWT page renders a 3-column layout with no pagination UI — ask for the
  // full (bounded) set so all of a tenant's strengths/weaknesses/trends are
  // visible. Backend caps pageSize at 100.
  const effectiveParams = { pageSize: '100', ...(params ?? {}) };
  return useQuery({
    queryKey: [...rorosKeys.overarching.swtItems, effectiveParams] as const,
    queryFn: () => RorosOverarchingService.swtItems.list(effectiveParams),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosSwtItem(id: string) {
  return useQuery({
    queryKey: rorosKeys.overarching.swtItem(id),
    queryFn: () => RorosOverarchingService.swtItems.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosSwtItemCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosOverarchingService.swtItems.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.swtItems });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

export function useRorosSwtItemUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosOverarchingService.swtItems.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.swtItems });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.swtItem(id) });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
    },
  });
}

export function useRorosSwtItemRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosOverarchingService.swtItems.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.swtItems });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.all });
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.completion });
    },
  });
}

export function useRorosSwtItemReorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id: string; sortOrder: number }[]) => RorosOverarchingService.swtItems.reorder(items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.overarching.swtItems });
    },
  });
}

// ─── Field Registry ────────────────────────────────────────────────────────

const FIELD_REGISTRY_STALE_TIME = 1000 * 60 * 60; // 1 hour
const FIELD_REGISTRY_GC_TIME = 1000 * 60 * 60 * 24; // 24 hours

export function useRorosFieldRegistry() {
  return useQuery({
    queryKey: rorosKeys.overarching.fieldRegistry,
    queryFn: () => RorosOverarchingService.fieldRegistry.list(),
    staleTime: FIELD_REGISTRY_STALE_TIME,
    gcTime: FIELD_REGISTRY_GC_TIME,
  });
}

export function useRorosFieldRegistryMatch(
  text: string,
  locale: 'en' | 'de' = 'en',
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: rorosKeys.overarching.fieldRegistryMatch(text, locale),
    queryFn: () => RorosOverarchingService.fieldRegistry.match(text, locale),
    staleTime: 0,
    gcTime: 0,
    enabled: opts.enabled ?? false,
  });
}

// ─── AI Research (B28) ────────────────────────────────────────────────────

export function useAiProviderConfigured() {
  return useQuery({
    queryKey: ['roros', 'overarching', 'ai-provider-status'] as const,
    queryFn: () => RorosOverarchingService.research.aiProviderStatus(),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    select: (data) => data.configured,
  });
}

export function useTriggerCompanyProfileResearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (overwriteMode: 'fill_only' | 'overwrite') =>
      RorosOverarchingService.research.trigger(overwriteMode),
    onSuccess: () => {
      // Data will be refreshed on WS research-completed event;
      // invalidate immediately so the job ID is fresh.
      qc.invalidateQueries({ queryKey: ['roros', 'overarching', 'ai-provider-status'] });
    },
  });
}
